import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';

export interface TradingAccount {
    id: string;
    account_number?: number; // Auto-increment account number (e.g., 50000, 50250, 50500)
    customer_id: string;
    base_currency: string;
    leverage: number;
    balance: number;
    equity: number;
    used_margin: number;
    free_margin: number;
    margin_level: number;
    unrealized_pnl: number;
    daily_pnl: number;
    total_deposits: number;
    total_bonuses: number;
    total_withdrawals: number;
    margin_status: {
        can_trade: boolean;
        margin_call: boolean;
        liquidation_warning: boolean;
    };
    created_at: string;
    updated_at: string;
}

export interface TradingAccountsResponse {
    accounts: TradingAccount[];
    total_accounts: number;
    currencies: string[];
}

// Get trading accounts for authenticated customer
const fetchTradingAccounts = async () => {
    const { data } = await httpClient.get<ApiResponse<TradingAccountsResponse>>(
        API_ENDPOINTS.TRADER.BALANCE.TRADING_ACCOUNTS
    );
    return data.data;
};

export const useTradingAccounts = (options?: { refetchInterval?: number }) => {
    return useQuery<TradingAccountsResponse>({
        queryKey: [API_ENDPOINTS.TRADER.BALANCE.TRADING_ACCOUNTS],
        queryFn: fetchTradingAccounts,
        refetchInterval: options?.refetchInterval ?? 10000, // Default: Refresh every 10 seconds for real-time updates
    });
};

// Get trading account details
const fetchTradingAccountDetails = async (accountId: string) => {
    const { data } = await httpClient.get<ApiResponse<TradingAccountsResponse>>(
        `${API_ENDPOINTS.TRADER.BALANCE.TRADING_ACCOUNTS}/${accountId}`
    );
    return data.data;
};

// Get a specific trading account by ID
export const useTradingAccount = (accountId: string) => {
    return useQuery<TradingAccount>({
        queryKey: [API_ENDPOINTS.TRADER.BALANCE.TRADING_ACCOUNTS, accountId],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<TradingAccountsResponse>>(
                API_ENDPOINTS.TRADER.BALANCE.TRADING_ACCOUNTS
            );
            const account = data.data.accounts.find(acc => acc.id === accountId);
            if (!account) {
                throw new Error('Trading account not found');
            }
            return account;
        },
        enabled: !!accountId,
        refetchInterval: 10000,
    });
};

// Get trading accounts for a specific customer
const fetchCustomerTradingAccounts = async (customerId: string) => {
    const { data } = await httpClient.get<ApiResponse<TradingAccount[]>>(
        API_ENDPOINTS.CRM.CUSTOMERS.ACCOUNTS(customerId)
    );
    // Transform the response to match the expected TradingAccountsResponse format
    return {
        accounts: data.data,
        total_accounts: data.data.length,
        currencies: [...new Set(data.data.map(account => account.base_currency))],
    };
};

// Hook to get trading accounts for a specific customer
export const useCustomerTradingAccounts = (customerId: string) => {
    return useQuery<TradingAccountsResponse>({
        queryKey: ['customer-accounts', customerId],
        queryFn: () => fetchCustomerTradingAccounts(customerId),
        enabled: !!customerId,
    });
};

// Interface for creating a trading account
export interface CreateTradingAccountRequest {
    customer_id: string;
    base_currency: string;
    balance: number;
    leverage?: number;
}

// Interface for updating a trading account
export interface UpdateTradingAccountRequest {
    base_currency?: string;
    leverage?: number;
    balance?: number;
    equity?: number;
    used_margin?: number;
    free_margin?: number;
    margin_level?: number;
    unrealized_pnl?: number;
    daily_pnl?: number;
    total_deposits?: number;
    total_withdrawals?: number;
}

// Create a trading account for a customer
export const useCreateTradingAccount = (customerId: string) => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<TradingAccount>, Error, CreateTradingAccountRequest>({
        mutationFn: async data => {
            const response = await httpClient.post<ApiResponse<TradingAccount>>(
                API_ENDPOINTS.CRM.CUSTOMERS.ACCOUNTS(customerId),
                data
            );
            return response.data;
        },
        onSuccess: () => {
            // Invalidate relevant queries to refresh data
            queryClient.invalidateQueries({ queryKey: ['customer-accounts', customerId] });
        },
    });
};

// Update a trading account
export const useUpdateTradingAccount = (customerId: string, accountId: string) => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<TradingAccount>, Error, UpdateTradingAccountRequest>({
        mutationFn: async data => {
            const response = await httpClient.put<ApiResponse<TradingAccount>>(
                `${API_ENDPOINTS.CRM.CUSTOMERS.ACCOUNTS(customerId)}/${accountId}`,
                data
            );
            return response.data;
        },
        onSuccess: () => {
            // Invalidate relevant queries to refresh data
            queryClient.invalidateQueries({ queryKey: ['customer-accounts', customerId] });
            queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.TRADER.BALANCE.TRADING_ACCOUNTS, accountId],
            });
        },
    });
};
